<template>
  <div
    class="card shadow bb-card armour-card"
    :class="{
      'upper-lower-card': isUpperOrLower,
      'limb-base-card': isLeftRightBase,
      'armour-empty': isEmpty,
      'armour-head-card': isHeadLocation,
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="card-body rounded armour-card-body">
      <!-- =========================================================== -->
      <!--   HEADER   -->
      <!-- =========================================================== -->
      <!-- --------- -->
      <!--   ROW 1   -->
      <!-- --------- -->
      <div class="row mt-1 mb-2 armour-card-header-row">
        <!-- ------------------------ -->
        <!--   armour location name   -->
        <!-- ------------------------ -->
        <div
          class="col-12"
          id="location_name"
        >
          {{ al.armour_location_name }}
          <span
            v-if="al.hit_location_roll"
            class="armour-hit-location-roll"
          >
            [{{ al.hit_location_roll }}]
          </span>
        </div>
      </div>
      <!-- --------- -->
      <!--   ROW 2   -->
      <!-- --------- -->
      <template v-if="!limbApFields">
        <div
          class="row mb-2 gx-2 align-items-start justify-content-center armour-inline-stats-row armour-head-stats-row"
        >
          <div class="col-2 armour-ap-column armour-stacked-ap-placeholder">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="getHighestAP"
                  id="armour_ap_placeholder"
                  disabled
                  tabindex="-1"
                  aria-hidden="true"
                />
                <label
                  class="bb-float-label optional"
                  for="armour_ap_placeholder"
                  aria-hidden="true"
                  >AP</label
                >
              </div>
            </div>
          </div>
          <div class="col-7 armour-cur-hp-column armour-head-cur-hp-column">
            <div class="armour-head-counter-wrapper">
              <template v-if="readonlyHpCur">
                <div class="d-flex align-items-center armour-ap-counter-match">
                  <span
                    class="btn btn-lg armour-counter-button-placeholder"
                    aria-hidden="true"
                    >-</span
                  >
                  <div class="col form-group ms-2 me-2">
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-center"
                        :value="Number(al.hp_cur) || 0"
                        :id="counterRef"
                        disabled
                      />
                      <label
                        class="bb-float-label optional"
                        :for="counterRef"
                        >Cur HP</label
                      >
                    </div>
                  </div>
                  <span
                    class="btn btn-lg armour-counter-button-placeholder"
                    aria-hidden="true"
                    >+</span
                  >
                </div>
              </template>
              <template v-else>
                <CounterComponent
                  :pass_ref="counterRef"
                  :pass_label="'Cur HP'"
                  :pass_count="Number(al.hp_cur) || 0"
                  :pass_min="counterMin"
                  :pass_max="counterMax"
                  :pass_digits="2"
                  :disabled="readonlyHpCur"
                />
              </template>
            </div>
          </div>
          <div
            v-if="showsTotalHpField"
            class="col-3 armour-total-hp-column"
          >
            <div class="form-group w-100">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="displayBodyLocationHp"
                  id="armour_total_hp"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="armour_total_hp"
                  >Total HP</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2 justify-content-center">
          <div class="col-8 mx-auto armour-ap-column armour-ap-column-stacked">
            <div class="d-flex align-items-center armour-ap-counter-match">
              <span
                class="btn btn-lg armour-counter-button-placeholder"
                aria-hidden="true"
                >-</span
              >
              <div class="col form-group ms-2 me-2">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="getHighestAP"
                    id="armour_ap"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="armour_ap"
                    >AP</label
                  >
                </div>
              </div>
              <span
                class="btn btn-lg armour-counter-button-placeholder"
                aria-hidden="true"
                >+</span
              >
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <template v-if="readonlyHpCur">
          <div
            :class="
              showsTotalHpField
                ? 'row mb-2 gx-2 align-items-start justify-content-center armour-inline-stats-row armour-wide-stats-row'
                : 'row mb-2 justify-content-center'
            "
          >
            <div
              :class="
                showsTotalHpField
                  ? 'col-6 armour-cur-hp-column armour-wide-cur-hp-column'
                  : 'col-6'
              "
            >
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center armour-segment-ap-input"
                    :value="Number(al.hp_cur) || 0"
                    :id="counterRef"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    :for="counterRef"
                    >Cur HP</label
                  >
                </div>
              </div>
            </div>
            <div
              v-if="showsTotalHpField"
              class="col-6 armour-total-hp-column"
            >
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="displayBodyLocationHp"
                    id="armour_total_hp"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="armour_total_hp"
                    >Total HP</label
                  >
                </div>
              </div>
            </div>
          </div>
        </template>
        <div
          v-else
          :class="
            showsTotalHpField
              ? 'row mb-2 gx-2 align-items-start justify-content-center armour-inline-stats-row armour-wide-stats-row'
              : 'row mb-2 gx-2 align-items-start justify-content-center armour-inline-stats-row'
          "
        >
          <div
            v-if="showsTotalHpField"
            class="armour-limb-stats-spacer"
            aria-hidden="true"
          ></div>
          <div
            :class="
              showsTotalHpField
                ? 'col-6 armour-cur-hp-column armour-wide-cur-hp-column'
                : 'col-12 armour-cur-hp-column'
            "
          >
            <div
              :class="showsTotalHpField ? 'armour-wide-counter-wrapper' : ''"
            >
              <CounterComponent
                :pass_ref="counterRef"
                :pass_label="'Cur HP'"
                :pass_count="Number(al.hp_cur) || 0"
                :pass_min="counterMin"
                :pass_max="counterMax"
                :pass_digits="2"
                :disabled="readonlyHpCur"
              />
            </div>
          </div>
          <div
            v-if="showsTotalHpField"
            class="col-6 armour-total-hp-column"
          >
            <div class="form-group w-100">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="displayBodyLocationHp"
                  id="armour_total_hp"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="armour_total_hp"
                  >Total HP</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-6">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center armour-segment-ap-input"
                  :value="limbApFields.upper"
                  id="armour_ap_upper"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="armour_ap_upper"
                  >Upper AP</label
                >
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center armour-segment-ap-input"
                  :value="limbApFields.lower"
                  id="armour_ap_lower"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="armour_ap_lower"
                  >Lower AP</label
                >
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CounterComponent from './Counter.vue'

export default {
  name: 'CombatArmourCard',
  //========================================
  // component
  //-----------
  components: {
    CounterComponent,
  },
  //========================================
  // props
  //-------
  props: {
    al: {
      // armour location
      type: Object,
      required: true,
    },
    bodyLocationHp: {
      type: [Number, String],
      default: '',
    },
    counterMax: {
      type: Number,
      default: 0,
    },
    counterMin: {
      type: Number,
      default: 0,
    },
    readonlyHpCur: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-hp-cur'],
  //========================================
  // data
  //------
  data() {
    return {
      counterListener: null,
      pendingHpCur: null,
      pendingHpCurEmitHandle: null,
      pendingHpCurUsesAnimationFrame: false,
    }
  },
  //========================================
  // computed
  //----------
  computed: {
    highestEquippedArmour() {
      const equipped = Array.isArray(this.al?.equipped_armour)
        ? this.al.equipped_armour
        : []

      if (equipped.length === 0) {
        return null
      }

      const highestAp = Number(this.al?.highest_ap)
      if (!Number.isNaN(highestAp)) {
        const matchingAp = equipped.filter(
          (item) => Number(item?.ap) === highestAp,
        )
        if (matchingAp.length > 0) {
          return matchingAp.reduce((best, item) => {
            if (!best) return item
            const itemLayer = Number(item?.layer) || 0
            const bestLayer = Number(best?.layer) || 0
            if (itemLayer !== bestLayer)
              return itemLayer > bestLayer ? item : best
            const itemHp = Number(item?.hp) || 0
            const bestHp = Number(best?.hp) || 0
            return itemHp > bestHp ? item : best
          }, null)
        }
      }

      return equipped.reduce((best, item) => {
        if (!best) return item
        const itemAp = Number(item?.ap) || 0
        const bestAp = Number(best?.ap) || 0
        if (itemAp !== bestAp) return itemAp > bestAp ? item : best
        const itemLayer = Number(item?.layer) || 0
        const bestLayer = Number(best?.layer) || 0
        if (itemLayer !== bestLayer) return itemLayer > bestLayer ? item : best
        const itemHp = Number(item?.hp) || 0
        const bestHp = Number(best?.hp) || 0
        return itemHp > bestHp ? item : best
      }, null)
    },
    isUpperOrLower() {
      if (!this.al || !this.al.armour_location_name) return false
      const name = this.al.armour_location_name.toLowerCase()
      return name.startsWith('upper ') || name.startsWith('lower ')
    },
    isLeftRightBase() {
      if (!this.al || !this.al.armour_location_name) return false
      const name = this.al.armour_location_name.toLowerCase()
      const isUpperLower =
        name.startsWith('upper ') || name.startsWith('lower ')
      const isBaseLimb = name.startsWith('left ') || name.startsWith('right ')
      // base left/right (e.g. "Left Arm", "Right Leg") but not upper/lower variants
      return isBaseLimb && !isUpperLower
    },
    normalizedLocationName() {
      if (!this.al || !this.al.armour_location_name) return ''
      return String(this.al.armour_location_name).trim().toLowerCase()
    },
    isHeadLocation() {
      return this.normalizedLocationName === 'head'
    },
    showsTotalHpField() {
      return this.bodyLocationHp !== '' && this.bodyLocationHp !== null
    },
    displayBodyLocationHp() {
      if (!this.showsTotalHpField) {
        return ''
      }

      return `${this.bodyLocationHp}`
    },
    getHighestAP() {
      if (!this.highestEquippedArmour) {
        return '0'
      }

      return `${this.highestEquippedArmour.ap}`
    },
    equippedCount() {
      return Array.isArray(this.al.equipped_armour)
        ? this.al.equipped_armour.length
        : 0
    },
    hasMultipleEquipped() {
      return (
        Array.isArray(this.al.equipped_armour) &&
        this.al.equipped_armour.length > 1
      )
    },
    sortedEquipped() {
      const equipped = Array.isArray(this.al.equipped_armour)
        ? [...this.al.equipped_armour]
        : []
      // sort by descending layer
      return equipped.sort((a, b) => (b.layer || 0) - (a.layer || 0))
    },
    limbApFields() {
      if (!this.isLeftRightBase) {
        return null
      }

      const segments = Array.isArray(this.al?.limb_segments)
        ? this.al.limb_segments
        : []
      const baseAp = Number(this.al?.highest_ap) || 0
      const getSegmentAp = (segmentNamePart) => {
        const match = segments.find((segment) =>
          String(segment.armour_location_name || '')
            .toLowerCase()
            .includes(segmentNamePart),
        )

        if (!match) {
          return `${baseAp}`
        }

        const segmentAp = Number(match.highest_ap) || 0
        return `${Math.max(segmentAp, baseAp)}`
      }

      return {
        upper: getSegmentAp('upper'),
        lower: getSegmentAp('lower'),
      }
    },
    isEmpty() {
      const noEquipped =
        !Array.isArray(this.al.equipped_armour) ||
        this.al.equipped_armour.length === 0
      const noAp = !this.al.highest_ap || Number(this.al.highest_ap) === 0
      return noEquipped && noAp
    },
    counterRef() {
      return `armour_cur_hp_${this.al?.id ?? 'unknown'}`
    },
  },
  mounted() {
    this.counterListener = (value) => {
      if (value === null || value === undefined || value === '') {
        return
      }

      const hpCur = Number(value)
      if (Number.isNaN(hpCur)) {
        return
      }

      this.pendingHpCur = hpCur
      this.scheduleHpCurEmit()
    }

    this.emitter.on(this.counterRef, this.counterListener)
  },
  beforeUnmount() {
    this.cancelHpCurEmit()

    if (this.counterListener) {
      this.emitter.off(this.counterRef, this.counterListener)
      this.counterListener = null
    }
  },
  //========================================
  // methods
  //---------
  methods: {
    scheduleHpCurEmit() {
      this.cancelHpCurEmit()

      const emitUpdate = () => {
        this.pendingHpCurEmitHandle = null

        if (this.pendingHpCur === null || this.pendingHpCur === undefined) {
          return
        }

        this.$emit('update-hp-cur', {
          id: this.al?.id,
          hp_cur: this.pendingHpCur,
        })
      }

      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        this.pendingHpCurUsesAnimationFrame = true
        this.pendingHpCurEmitHandle = window.requestAnimationFrame(emitUpdate)
        return
      }

      this.pendingHpCurUsesAnimationFrame = false
      this.pendingHpCurEmitHandle = setTimeout(emitUpdate, 0)
    },
    cancelHpCurEmit() {
      if (this.pendingHpCurEmitHandle === null) {
        return
      }

      if (
        this.pendingHpCurUsesAnimationFrame &&
        typeof window !== 'undefined' &&
        window.cancelAnimationFrame
      ) {
        window.cancelAnimationFrame(this.pendingHpCurEmitHandle)
      } else {
        clearTimeout(this.pendingHpCurEmitHandle)
      }

      this.pendingHpCurEmitHandle = null
    },
  },
}
</script>

<style scoped>
.btn-lg {
  font-size: 30px !important;
}

.btn.btn-primary[disabled] {
  background-color: #737373 !important;
  color: white !important;
  border-color: grey !important;
  opacity: 1 !important;
}

/* body type location HP */
#location_name,
#location_hp {
  color: #ffffff;
  font-size: 1.6rem;
  font-weight: 600;
}

.armour-hit-location-roll {
  color: #25bfda;
  margin-left: 0.5rem;
}

/* smaller font size for input fields */
#armour_ap,
#armour_total_hp {
  font-size: 1.7rem !important;
}

.armour-ap-column {
  display: flex;
  align-items: stretch;
  padding-right: 0.2rem;
}

.armour-total-hp-column {
  display: flex;
  align-items: stretch;
  padding-left: 0;
  padding-right: 0;
}

.armour-ap-column-stacked {
  justify-content: center;
  padding-right: 0;
}

.armour-stacked-ap-placeholder {
  visibility: hidden;
  pointer-events: none;
}

.armour-head-card {
  min-height: 100%;
}

.armour-counter-button-placeholder {
  visibility: hidden;
  pointer-events: none;
}

.bb-readonly-counter-shell :deep(button) {
  visibility: hidden;
  pointer-events: none;
}

.armour-cur-hp-column {
  display: flex;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.armour-head-cur-hp-column > div {
  width: 100%;
}

.armour-head-counter-wrapper {
  width: 100%;
}

.armour-head-cur-hp-column :deep(.d-flex.align-items-center) {
  width: 100%;
}

.armour-head-counter-wrapper :deep(.form-group.ms-2.me-2) {
  flex: 1 1 auto;
  min-width: 0;
}

.armour-head-counter-wrapper :deep(.form-control) {
  width: 100%;
  min-width: 0;
}

.armour-wide-cur-hp-column > div {
  width: 100%;
}

.armour-wide-counter-wrapper {
  width: 100%;
}

.armour-head-cur-hp-column {
  transform: translateX(-0.75rem);
}

.armour-wide-cur-hp-column {
  transform: translateX(-0.75rem);
}

.armour-total-hp-column {
  transform: translateX(-0.3rem);
}

.armour-wide-cur-hp-column :deep(.d-flex.align-items-center) {
  width: 100%;
}

.armour-wide-counter-wrapper :deep(.form-group.ms-2.me-2) {
  flex: 1 1 auto;
  min-width: 0;
}

.armour-wide-counter-wrapper :deep(.form-control) {
  width: 100%;
  min-width: 0;
}

.armour-inline-stats-row > .armour-cur-hp-column,
.armour-inline-stats-row > .armour-total-hp-column,
.armour-inline-stats-row > .armour-ap-column {
  min-width: 0;
}

.armour-head-stats-row {
  flex-wrap: nowrap;
  align-items: center !important;
}

.armour-head-stats-row > .armour-ap-column {
  flex: 0 0 10% !important;
  max-width: 10% !important;
}

.armour-head-stats-row > .armour-cur-hp-column {
  flex: 0 0 66.666667% !important;
  max-width: 66.666667% !important;
}

.armour-head-stats-row > .armour-total-hp-column {
  flex: 0 0 23.333333% !important;
  max-width: 23.333333% !important;
}

.armour-wide-stats-row {
  flex-wrap: nowrap;
  align-items: center !important;
}

.armour-limb-stats-spacer {
  flex: 0 0 0 !important;
  max-width: 0 !important;
  min-width: 0;
  padding: 0;
}

.armour-wide-stats-row > .armour-cur-hp-column {
  flex: 0 0 66.666667% !important;
  max-width: 66.666667% !important;
}

.armour-wide-stats-row > .armour-total-hp-column {
  flex: 0 0 23.333333% !important;
  max-width: 23.333333% !important;
}

/* tighten spacing between input fields on this card */
.armour-card-body {
  padding: 0.5rem 0.65rem 0.4rem;
  margin: 0.2rem;
}

.armour-card-header-row {
  min-height: 0;
}

.armour-card .form-group {
  margin-bottom: 0.15rem;
}

.armour-segment-ap-input {
  font-size: 1.7rem !important;
}

/* ensure overlay positions relative to the whole card */
.armour-card {
  position: relative;
  overflow: hidden;
  width: 100%;
  background-color: var(--combat-armour-card-bg, #3c3c3c) !important;
}
</style>
